import React, { lazy } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

// project imports
import Loadable, { lazyWithRetry } from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import { PrivateRoute } from './PrivateRoute';
import RoleBasedRoute from './RoleBasedRoute';

// render - dashboard
const DashboardDefault = Loadable(lazyWithRetry(() => import('features/dashboard/default')));

// render - component overview
const Color = Loadable(lazyWithRetry(() => import('features/component-overview/color')));
const Typography = Loadable(lazyWithRetry(() => import('features/component-overview/typography')));
const Shadow = Loadable(lazyWithRetry(() => import('features/component-overview/shadows')));
const PageNotFoundPage = Loadable(lazyWithRetry(() => import('features/extra-pages/PageNotFind')));

// render - pages
const ListingOrgsCards = Loadable(lazyWithRetry(() => import('features/organizations/pages/OrganizationsListPage')));
const ListingProjectsCards = Loadable(lazyWithRetry(() => import('features/projects/ListingProjectsCards')));
const UserTable = Loadable(lazyWithRetry(() => import('features/users/ListingUser')));
const OrgAdminDashboard = Loadable(lazyWithRetry(() => import('features/dashboard/orgAdmin/index')));
const ListingModuleCards = Loadable(lazyWithRetry(() => import('features/modules/pages/ListingModuleCards')));
const ModuleDetailsPage = Loadable(lazyWithRetry(() => import('features/modules/pages/ModuleDetailsPage')));
const EnvironmentPage = Loadable(lazyWithRetry(() => import('features/environments/index')));
const ReportPage = Loadable(lazyWithRetry(() => import('features/reports/index')));

// ✅ Reports feature
const ModuleExecutions = Loadable(lazyWithRetry(() => import('features/reports/pages/ModuleExecutions')));
const TestCaseReport = Loadable(lazyWithRetry(() => import('features/reports/components/TestCaseReport')));

const JiraExplorer = Loadable(lazyWithRetry(() => import('features/jira/JiraExplorer')));
const TicketsBoard = Loadable(lazyWithRetry(() => import('features/tickets/TicketsBoard')));
const Configuration = Loadable(lazyWithRetry(() => import('features/cofiguration/configuration')));
// TicketStoreProvider (small component, no lazy loading needed)
import { TicketStoreProvider } from 'features/tickets/store/TicketStore';
const ApiEditorPage = Loadable(lazyWithRetry(() => import('features/api-automation/pages/ApiEditorPage')));
const ApiListPage = Loadable(lazyWithRetry(() => import('features/api-automation/pages/ApiListPage')));
const ApiAutomationPage = Loadable(lazyWithRetry(() => import('features/api-automation/pages/ApiAutomationPage')));
// API Automation (unified create + edit)
const ApiAutomationCreateTestCasePage = Loadable(lazy(() => import('features/api-automation/pages/ApiAutomationCreateTestCasePage')));
const ApiAutomationCreateTestSetPage = Loadable(lazy(() => import('features/api-automation/pages/ApiAutomationCreateTestSetPage')));
const InspectorPage = Loadable(lazyWithRetry(() => import('features/inspector/pages/InspectorPage')));
const TestManagementPage = Loadable(lazyWithRetry(() => import('features/test-management/pages/TestManagementPage')));

// ==============================|| MAIN ROUTING (Protected) ||============================== //

const REPORT_ROLES = ['ROLE_SUPER_ADMIN', 'ROLE_MEMBER', 'ROLE_ORG_ADMIN', 'ROLE_PROJECT_MANAGER'];
const MODULE_ROLES = ['ROLE_SUPER_ADMIN', 'ROLE_MEMBER', 'ROLE_ORG_ADMIN', 'ROLE_PROJECT_MANAGER'];

const MainRoutes: RouteObject = {
  path: '/',
  element: (
    <PrivateRoute>
      <DashboardLayout />
    </PrivateRoute>
  ),
  children: [
    // ── Inspector Route ──────────────────────────────────────────
    {
      path: 'inspector/:moduleUid/:screenUid/:deviceConfigUid',
      element: <InspectorPage />
    },

    // ── Default redirect ──────────────────────────────────────────
    {
      path: '/',
      element: <Navigate to="/dashboard" replace />
    },

    // ── Dashboard ─────────────────────────────────────────────────
    {
      path: 'dashboard',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <DashboardDefault />
        </RoleBasedRoute>
      )
    },
    // {
    //   path: '/dashboard/1',
    //   element: (
    //     <RoleBasedRoute allowedRoles={['ROLE_ORG_ADMIN']}>
    //       <OrgAdminDashboard />
    //     </RoleBasedRoute>
    //   )
    // },

    // ── Component overview ────────────────────────────────────────
    {
      path: 'typography',
      element: <Typography />
    },
    {
      path: 'color',
      element: <Color />
    },
    {
      path: 'shadow',
      element: <Shadow />
    },
    {
      path: 'not-found',
      element: <PageNotFoundPage />
    },

    // ── ✅ Jira Explorer ──────────────────────────────────────────
    {
      path: 'jira',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <JiraExplorer />
        </RoleBasedRoute>
      )
    },

    // ── Tickets ────────────────────────────────────────────────
    // (TicketsBoard syncs its internal view to /tickets/:view, so both paths must exist)
    {
      path: 'tickets',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <TicketStoreProvider>
            <TicketsBoard />
          </TicketStoreProvider>
        </RoleBasedRoute>
      )
    },
    {
      path: 'tickets/:view',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <TicketStoreProvider>
            <TicketsBoard />
          </TicketStoreProvider>
        </RoleBasedRoute>
      )
    },

    {
      path: 'configuration',
      element: (
        <RoleBasedRoute allowedRoles={['ROLE_SUPER_ADMIN']}>
          <Configuration />
        </RoleBasedRoute>
      )
    },

    // ── Users ─────────────────────────────────────────────────────
    {
      path: 'users',
      element: (
        <RoleBasedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ORG_ADMIN']}>
          <UserTable />
        </RoleBasedRoute>
      )
    },

    // ── Organizations ─────────────────────────────────────────────
    {
      path: 'organizations',
      element: (
        <RoleBasedRoute allowedRoles={['ROLE_SUPER_ADMIN']}>
          <ListingOrgsCards />
        </RoleBasedRoute>
      )
    },

    // ── Environments ──────────────────────────────────────────────
    // {
    //   path: 'environments',
    //   element: (
    //     <RoleBasedRoute allowedRoles={['ROLE_MEMBER']}>
    //       <EnvironmentPage />
    //     </RoleBasedRoute>
    //   )
    // },

    // ── Global Reports ────────────────────────────────────────────
    {
      path: 'reports',
      element: (
        <RoleBasedRoute allowedRoles={REPORT_ROLES}>
          <ReportPage />
        </RoleBasedRoute>
      )
    },

    // ── Projects ──────────────────────────────────────────────────
    {
      path: 'projects',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <ListingProjectsCards />
        </RoleBasedRoute>
      )
    },

    // ── Test Management ────────────────────────────────────────────
    {
      path: 'test-management',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <TestManagementPage />
        </RoleBasedRoute>
      )
    },

    // ── Modules list by project ───────────────────────────────────
    {
      path: 'projects/:projectUid/modules',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <ListingModuleCards />
        </RoleBasedRoute>
      )
    },

    // ── API Automation ────────────────────────────────────────────
    // (Must be defined BEFORE the :tab wildcard so /apis is not captured as a tab)
    {
      path: 'projects/:projectUid/modules/:moduleUid/apis',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <ApiAutomationPage />
        </RoleBasedRoute>
      )
    },
    {
      path: 'projects/:projectUid/modules/:moduleUid/apis/create',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <ApiEditorPage />
        </RoleBasedRoute>
      )
    },
    {
      path: 'projects/:projectUid/modules/:moduleUid/apis/testcases/create',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <ApiAutomationCreateTestCasePage />
        </RoleBasedRoute>
      )
    },
    {
      path: 'projects/:projectUid/modules/:moduleUid/apis/testsets/create',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <ApiAutomationCreateTestSetPage />
        </RoleBasedRoute>
      )
    },
    {
      path: 'projects/:projectUid/modules/:moduleUid/apis/testsets/:testSetUid/edit',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <ApiAutomationCreateTestSetPage />
        </RoleBasedRoute>
      )
    },
    {
      path: 'projects/:projectUid/modules/:moduleUid/apis/testcases/:testCaseUid/edit',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <ApiAutomationCreateTestCasePage />
        </RoleBasedRoute>
      )
    },
    {
      path: 'projects/:projectUid/modules/:moduleUid/apis/:apiUid/edit',
      element: (
        <RoleBasedRoute allowedRoles={MODULE_ROLES}>
          <ApiEditorPage />
        </RoleBasedRoute>
      )
    },

    {
      path: 'reports/:uid',
      element: (
        <RoleBasedRoute allowedRoles={REPORT_ROLES}>
          <TicketStoreProvider>
            <TestCaseReport />
          </TicketStoreProvider>
        </RoleBasedRoute>
      )
    },

    // ── Module details + nested reports ───────────────────────────
    {
      path: '/projects/:projectUid/modules/:moduleUid',
      children: [
        {
          index: true,
          element: <Navigate to="devices" replace />
        },
        {
          path: ':tab',
          element: (
            <RoleBasedRoute allowedRoles={MODULE_ROLES}>
              <ModuleDetailsPage />
            </RoleBasedRoute>
          )
        },
        {
          path: ':tab/:screenUid',
          element: (
            <RoleBasedRoute allowedRoles={MODULE_ROLES}>
              <ModuleDetailsPage />
            </RoleBasedRoute>
          )
        },
        {
          path: ':tab/:reports',
          element: (
            <RoleBasedRoute allowedRoles={REPORT_ROLES}>
              <ModuleExecutions />
            </RoleBasedRoute>
          )
        }
      ]
    }
  ]
};

export default MainRoutes;
